<script>
import SchedulerLayout from '../../../Layouts/SchedulerLayout.vue';
import Card from '../../../Components/Card.vue';
import { matchHeightByClass } from '../../../Modules/Helpers.js';
import Checkbox from '../../../Components/Inputs/Checkbox.vue';
import Check from '../../../Components/Icons/Check.vue';
import { formatNumberAsMoney } from '../../../Modules/Money.js';

export default {
  components: { Check, Checkbox, Card },

  layout: SchedulerLayout,

  props: {
    masterConfig: Object,
    kioskKey: String,
    selected: {
      type: Array,
      default: [],
    },
    agreed: Boolean,
    jobID: String,
    settings: Object,
    tireConnectData: Object,
  },

  emits: ['ready', 'update', 'next', 'preReady'],

  data() {
    return {
      searchQuery: '',
      loading: false,
      services: [],
      checked: false,
      selectedServices: [],
      queryServiceTokens: [],
      lockedServiceIds: [],
    };
  },

  computed: {
    filteredServices() {
      return this.services.filter((service) => {
        return service.job_name.toLowerCase().includes(this.searchQuery.toLowerCase());
      });
    },

    hasTerms() {
      return (
        this.settings?.slot_settings?.terms !== undefined &&
        this.settings?.slot_settings?.terms !== null &&
        this.settings?.slot_settings?.terms.length > 0
      );
    },

    isReady() {
      const hasService = this.selectedServices.length > 0 || this.services.length === 0;
      if (this.hasTerms) {
        return hasService && this.checked === true;
      }
      return hasService;
    },
  },

  watch: {
    isReady(newVal) {
      this.$emit('ready', newVal);
      if (newVal) {
        this.$emit('preReady', null);
      }
    },

    selectedServices(newVal) {
      if (newVal.length > 0 && this.hasTerms && !this.checked) {
        this.$emit('preReady', this.scrollToTerms);
      } else {
        this.$emit('preReady', null);
      }
    },

    checked(newVal) {
      if (newVal || this.selectedServices.length === 0) {
        this.$emit('preReady', null);
      }
    },

    tireConnectData: {
      handler() {
        this.checkAutoSelectTireService();
      },
      deep: true,
    },

    kioskKey(newKey) {
      if (newKey && this.services.length === 0) {
        this.getServices();
      }
    },
  },

  mounted() {
    this.selectedServices = this.selected;
    this.checked = this.agreed;
    this.queryServiceTokens = this.parseServiceTokensFromQuery();
    this.$emit('ready', this.isReady);
    if (this.isReady) {
      this.$emit('preReady', null);
    }
    if (this.kioskKey) {
      this.getServices();
    }
  },

  methods: {
    formatNumberAsMoney,

    scrollToTerms() {
      this.$nextTick(() => {
        const container = this.$el.querySelector('.disclaimer-container');
        if (!container) return;

        const headerHeight = 60;
        const top = container.getBoundingClientRect().top + window.scrollY - headerHeight;
        window.scrollTo({ top, behavior: 'smooth' });

        setTimeout(() => {
          container.classList.add('disclaimer-error');
          setTimeout(() => container.classList.remove('disclaimer-error'), 2000);
        }, 300);
      });
    },

    parseServiceTokensFromQuery() {
      if (typeof window === 'undefined') {
        return [];
      }
      const params = new URLSearchParams(window.location.search);
      const servicesParam = params.get('services');
      if (!servicesParam) {
        return [];
      }
      return servicesParam
        .split(',')
        .map((token) => token.trim().toLowerCase())
        .filter((token) => token.length > 0);
    },

    serviceMatchesToken(service, token) {
      const jobId = String(service?.kiosk_job_id ?? '').toLowerCase();
      const serviceId = String(service?.id ?? '').toLowerCase();
      const jobName = String(service?.job_name ?? '').toLowerCase();
      if (token === jobId || token === serviceId) {
        return true;
      }
      return jobName.includes(token);
    },

    autoSelectServicesFromQuery() {
      if (this.queryServiceTokens.length === 0 || this.services.length === 0) {
        return;
      }
      let matched = false;
      this.services.forEach((service) => {
        const shouldAutoSelect = this.queryServiceTokens.some((token) =>
          this.serviceMatchesToken(service, token),
        );
        if (shouldAutoSelect && !this.isSelected(service)) {
          this.selectedServices = [...this.selectedServices, service];
          matched = true;
        }
      });
      this.$emit('update', { services: this.selectedServices });
      if (matched && this.shouldSkipServices()) {
        this.$nextTick(() => {
          this.$emit('next');
        });
      }
    },

    shouldSkipServices() {
      if (typeof window === 'undefined') return false;
      const params = new URLSearchParams(window.location.search);
      return params.get('skip_services') === '1';
    },

    setJob(job_id) {
      let job = this.services.find((job) => job.kiosk_job_id == job_id);
      if (job === undefined) return;
      this.toggleService(job);
    },

    isSelected(service) {
      return (
        this.selectedServices.findIndex(
          (selected) => selected.kiosk_job_id == service.kiosk_job_id,
        ) > -1
      );
    },

    isLockedService(service) {
      return this.lockedServiceIds.some((id) => id == service?.kiosk_job_id);
    },

    getIconClass(service) {
      if (service?.icon !== null && service?.icon?.length > 0) {
        return 'fa ' + service.icon;
      }
      return null;
    },

    getServices() {
      this.loading = true;
      axios
        .post(route('scheduler.services', { key: this.kioskKey }))
        .then((response) => {
          this.services = response.data.data.services;
          this.autoSelectServicesFromQuery();
          this.checkAutoSelectTireService();
          if (this.services.length === 0) {
            if (this.hasTerms) {
              // Don't skip — user must agree to terms first
              // isReady computed will handle enabling the next button
              // once checked, but we need selectedServices to be non-empty too.
              // Emit preReady to prompt them to scroll/agree if hasTerms.
              this.$emit('preReady', this.scrollToTerms);
            } else {
              this.$emit('next');
            }
          }
          if (this.jobID !== null) {
            this.setJob(this.jobID);
          }
          matchHeightByClass('service-grid', 'service-card');
        })
        .catch((error) => {
          console.log(error);
          this.$emit('next');
        })
        .finally(() => {
          this.loading = false;
        });
    },

    checkAutoSelectTireService() {
      if (
        !this.tireConnectData ||
        typeof this.tireConnectData !== 'object' ||
        this.services.length === 0
      )
        return;

      const tc = this.tireConnectData;
      const isValidPayload =
        tc.tires !== undefined ||
        tc.services !== undefined ||
        tc.quote !== undefined ||
        tc.vehicle !== undefined ||
        tc.customer !== undefined;

      if (!isValidPayload) return;

      let tireService = this.services.find((service) => service.tire_connect_service);
      if (!tireService) return;

      if (!this.lockedServiceIds.some((id) => id == tireService.kiosk_job_id)) {
        this.lockedServiceIds = [...this.lockedServiceIds, tireService.kiosk_job_id];
      }
      if (!this.isSelected(tireService)) {
        this.toggleService(tireService);
      }
    },

    toggleCheck() {
      this.checked = !this.checked;
      this.$emit('update', { checked: this.checked });
    },

    toggleService(service) {
      if (this.isSelected(service)) {
        if (this.isLockedService(service)) {
          return;
        }
        this.removeService(service);
      } else {
        this.selectedServices = [...this.selectedServices, service];
      }
      this.$emit('update', { services: this.selectedServices });
    },

    removeService(service) {
      if (this.isLockedService(service)) {
        return;
      }
      this.selectedServices = this.selectedServices.filter(
        (s) => s.kiosk_job_id !== service.kiosk_job_id,
      );
      this.$emit('update', {
        services: this.selectedServices,
      });
    },
  },
};
</script>

<template>
  <Card class="mt-3 services-card">
    <!-- Search Bar -->
    <input v-model="searchQuery" type="text" class="form-control mb-3" placeholder="Search" />

    <!-- Selected Services Chips -->
    <div v-if="selectedServices.length" class="mb-3">
      <h6>Selected Services:</h6>
      <div class="selected-services">
        <div
          v-for="service in selectedServices"
          :key="service.id"
          class="chip d-flex justify-content-between align-items-center"
        >
          <i :class="getIconClass(service)" class="mr-2" />
          {{ service.job_name }}
          <button
            v-if="!isLockedService(service)"
            class="close-btn"
            @click.stop="removeService(service)"
          >
            ×
          </button>
        </div>
      </div>
    </div>

    <!-- Scrollable Services Grid -->
    <div class="services-scroll-container">
      <div v-if="loading" class="text-center py-4 text-muted">
        <i class="fa fa-spinner fa-spin mr-2" /> Loading services...
      </div>
      <div v-else class="row services-grid m-0">
        <div
          v-for="service in filteredServices"
          :key="service.id"
          class="col-md-6 col-lg-4 col-xl-3 mb-3"
        >
          <div
            class="service-card card primary-color h-100"
            :class="{
              selected: isSelected(service),
              locked: isLockedService(service) && isSelected(service),
            }"
            @click="toggleService(service)"
          >
            <div class="card-body">
              <div class="d-flex justify-content-between">
                <h6 class="card-title">
                  <i :class="getIconClass(service) || 'fa fa-wrench'" class="mr-2 me-2" />
                  <template v-if="service.price !== null && service.price > 0">
                    {{ service.job_name }} — {{ formatNumberAsMoney(service.price) }}
                  </template>
                  <template v-else>
                    {{ service.job_name }}
                  </template>
                </h6>
              </div>
              <p class="card-text text-muted small">
                {{ service.job_description || '--' }}
              </p>
            </div>
          </div>
        </div>

        <div
          v-if="!loading && filteredServices.length === 0"
          class="col-12 text-center py-4 text-muted"
        >
          No services found.
        </div>
      </div>
    </div>

    <!-- Sticky Disclaimer -->
    <div v-if="hasTerms" class="disclaimer-container mt-3">
      <div class="disclaimer-scroll">
        <div class="disclaimer-text" id="disclaimerText">
          {{ settings.slot_settings.terms }}
        </div>
      </div>
      <div class="disclaimer-footer d-flex justify-content-end align-items-center pt-2 mt-2">
        <input
          type="checkbox"
          class="form-check-input custom-checkbox"
          id="disclaimerCheck"
          :checked="checked"
          @change="toggleCheck"
        />
        <label class="form-check-label ms-2" for="disclaimerCheck">I agree</label>
      </div>
    </div>
  </Card>
</template>

<style scoped>
/* ── Layout ─────────────────────────────────────────── */
.services-card {
  display: flex;
  flex-direction: column;
}

.services-scroll-container {
  max-height: 480px;
  overflow-y: auto;
  padding-right: 4px;
}

.services-scroll-container::-webkit-scrollbar {
  width: 6px;
}
.services-scroll-container::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}
.services-scroll-container::-webkit-scrollbar-thumb {
  background: #ccc;
  border-radius: 4px;
}
.services-scroll-container::-webkit-scrollbar-thumb:hover {
  background: #aaa;
}

/* ── Service Cards ───────────────────────────────────── */
.service-card {
  cursor: pointer;
  transition:
    transform 0.2s ease,
    box-shadow 0.2s ease;
  border: 1px solid #e5e5e5;
  color: #6c757d;
  padding: 10px 0;
}

.service-card.primary-color {
  border-color: var(--autoflow-scheduler-primary-color);
}

.service-card .card-title {
  color: var(--ink-primary-color);
}

.service-card .card-body {
  color: var(--ink-primary-color);
}

.service-card:hover {
  transform: scale(1.03);
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.1);
}

.service-card.selected {
  border: 2px solid #007bff;
  background-color: #e9f5ff;
}

.service-card.locked {
  cursor: default;
}

.service-card.locked:hover {
  transform: none;
  box-shadow: none;
}

/* ── Selected Service Chips ──────────────────────────── */
.selected-services {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.chip {
  background-color: #f8f9fa;
  border: 1px solid #ddd;
  padding: 5px 10px;
  border-radius: 15px;
  font-size: 14px;
  gap: 6px;
}

.close-btn {
  background: none;
  border: none;
  font-size: 16px;
  color: #ff4d4d;
  cursor: pointer;
  line-height: 1;
  padding: 0 2px;
}

.close-btn:hover {
  color: #cc0000;
}

/* ── Disclaimer ──────────────────────────────────────── */
.disclaimer-container {
  background-color: #f8f9fa;
  border: 2px solid #dee2e6;
  border-radius: 6px;
  padding: 12px 16px;
  flex-shrink: 0;
  transition:
    border-color 0.2s ease,
    background-color 0.2s ease;
}

.disclaimer-scroll {
  max-height: 180px;
  overflow-y: auto;
  padding-right: 6px;
}

.disclaimer-scroll::-webkit-scrollbar {
  width: 5px;
}
.disclaimer-scroll::-webkit-scrollbar-track {
  background: #e9ecef;
  border-radius: 4px;
}
.disclaimer-scroll::-webkit-scrollbar-thumb {
  background: #adb5bd;
  border-radius: 4px;
}
.disclaimer-scroll::-webkit-scrollbar-thumb:hover {
  background: #868e96;
}

.disclaimer-footer {
  border-top: 1px solid #dee2e6;
  transition: border-color 0.2s ease;
}

.disclaimer-text {
  font-weight: 600;
  color: var(--ink-primary-color);
  white-space: pre-wrap;
  transition: color 0.2s ease;
}

.custom-checkbox {
  transform: scale(1.5);
  filter: drop-shadow(2px 2px 4px rgba(0, 0, 0, 0.3));
  cursor: pointer;
  flex-shrink: 0;
}

.form-check-label {
  font-weight: 600;
  cursor: pointer;
  margin-bottom: 0;
  transition: color 0.2s ease;
}

/* ── Disclaimer Error State ──────────────────────────── */
@keyframes disclaimer-error-pulse {
  0% {
    border-color: #dc3545;
    box-shadow: 0 0 0 0 rgba(220, 53, 69, 0.5);
  }
  50% {
    border-color: #dc3545;
    box-shadow: 0 0 0 10px rgba(220, 53, 69, 0);
  }
  100% {
    border-color: #dc3545;
    box-shadow: 0 0 0 0 rgba(220, 53, 69, 0);
  }
}

.disclaimer-error {
  border-color: #dc3545 !important;
  background-color: #fff5f5 !important;
  animation: disclaimer-error-pulse 0.6s ease 2;
}

.disclaimer-error .disclaimer-text {
  color: #dc3545;
}

.disclaimer-error .disclaimer-footer {
  border-top-color: #dc3545;
}

.disclaimer-error .form-check-label {
  color: #dc3545;
}
</style>
